import React from 'react';

import { Box, InputAdornment, Typography } from '@mui/material';

import styled from '@emotion/styled';
import { useField } from 'formik';

import {
  ChipOption,
  ChipOptionsProps,
  ChipOptionsV2,
} from '../../../../../components/ChipOptions/ChipOptionsV2';
import { CurrencySign } from '../../../../../components/CurrencySign/CurrencySign';
import { StarterIcon } from '../../../../../components/Dialogs/CollabsFilters/ToolTip';
import { followerOptionsV2 } from '../../../../../components/Dialogs/CollabsFilters/options';
import FormikNumberField from '../../../../../components/Formiks/NumberField/NumberField';
import FormikTextField from '../../../../../components/Formiks/TextField/TextField';
import { HtmlTooltip } from '../../../../../components/HtmlTooltip/HtmlTooltip';
import { PremiumTooltip } from '../../../../../components/PremiumTooltip/PremiumTooltip';
import { OfferRequirementsV2 } from '../../../../../components/V2/OfferRequirements/OfferRequirementsV2';
import { OfferRequirements } from '../../../../../graphql/API';
import useSubscriptionAccess from '../../../../../hooks/useSubscriptionAccess';
import { useAppSelector } from '../../../../../redux/hooks';
import Icons from '../../../../../themes/icons';
import { extractSubscriptionPlan } from '../../../../SubscriptionPlans/common/helper';
import { Method } from '../../../ProductForm';
import { OFFER_REQUIREMENTS_V2 } from '../../../constants';
import { AttentionError } from '../../AttentionError';
import ShippingAvailability from '../../Shipping';
import SideBySide from '../../SideBySide';
import ReqRadioGroup from '../../formiks/OfferRequirement/OfferRequirement';
import { FormValue, Requirement, StepOneFormValue, StepTwoFormValue } from '../../schema';

type ChipOnChange = ChipOptionsProps['onChange'];

type MinimumFollowersProps = {
  label?: string;
  name?: string;
  defaultValue?: number;
  options: ChipOption[];
  showTooltip?: boolean;
};

const MinimumFollowersOptions: React.FC<MinimumFollowersProps> = ({
  label = 'Minimum followers',
  name = 'minFollowers',
  defaultValue = 0,
  options,
  showTooltip,
}) => {
  const [, , helper] = useField(name);

  const handleOnChange = React.useCallback<ChipOnChange>(
    (e) => {
      helper.setValue(e.value);
    },
    [helper],
  );

  return (
    <Box>
      <Box mb={'15px'} display={'flex'} alignItems={'center'} gap={'12px'}>
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
        {showTooltip && (
          <PremiumTooltip showStarterIcon>
            <Box display={'flex'} alignContent={'flex-start'} gap={'8px'}>
              <StarterIcon sx={{ margin: '0', fontSize: '18px', fill: '#40E5AF' }} />
              <Typography fontSize={'15px'} sx={{ textDecoration: 'underline', fontWeight: 500 }}>
                Upgrade
              </Typography>
            </Box>
          </PremiumTooltip>
        )}
      </Box>

      <ChipOptionsV2
        options={options}
        name={name}
        defaultValue={String(defaultValue)}
        onChange={handleOnChange}
      />
    </Box>
  );
};

type Values = StepOneFormValue & StepTwoFormValue;

type Props = {
  method?: Method;
  initialValues?: Values;
  values: Values;
  handlePersistFormDataStep?: (values: Partial<FormValue>) => void;
};

export const Form2: React.FC<Props> = ({
  method = 'insert',
  initialValues,
  values,
  handlePersistFormDataStep,
}) => {
  const { currency: venueCurrency } = useAppSelector((state) => state.venue.value) || {};
  const { subscription } = useAppSelector((state) => state.subscription.value);
  const subscriptionAccess = useSubscriptionAccess();

  const [field, , helper] = useField<Requirement>('requirements');

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const { fOptions } = React.useMemo(
    () => ({
      fOptions: followerOptionsV2(subscriptionAccess),
    }),
    [subscriptionAccess],
  );

  const noPremiumAccess = React.useMemo(
    () => !subscriptionAccess?.includes('PREMIUM_ACCESS_CONTROL'),
    [subscriptionAccess],
  );

  React.useEffect(() => {
    if (handlePersistFormDataStep) handlePersistFormDataStep({ ...(values as FormValue) });
  }, [values]);

  const handleOfferRequirementChange = (value: string) => {
    helper.setValue(value as Requirement);
  };

  return (
    <>
      <Box mb={5} display={'flex'} flexDirection={'column'} gap={2}>
        {OFFER_REQUIREMENTS_V2.map((requirement) => (
          <OfferRequirementsV2
            key={requirement.id}
            active={requirement.value.includes(field.value as never)}
            {...requirement}
            handleChange={() => {
              handleOfferRequirementChange(requirement.value[0]);
            }}
          />
        ))}
      </Box>
      {field.value === 'postOnTikTokVideo' && (
        <SideBySide shouldFlexEnd>
          <CustomBox>
            <Box display={'flex'} alignItems={'center'} gap={'8px'} mb={'15px'}>
              <Typography
                sx={{
                  fontSize: '15px',
                  fontWeight: 500,
                }}
              >
                Tag (optional)
              </Typography>
              <HtmlTooltip
                arrow
                placement='top'
                title={
                  <React.Fragment>
                    <Typography fontSize={'13px'} p={'12px'}>
                      Add your @TikTok handle to be tagged in posts
                    </Typography>
                  </React.Fragment>
                }
              >
                <Icons.InfoIcon />
              </HtmlTooltip>
            </Box>
            <FormikTextField
              id='txt-offer-value'
              name='tag'
              className='offer-input-field'
              placeholder='@Tag'
            />
          </CustomBox>
        </SideBySide>
      )}
      {[
        OfferRequirements.postOnInstagramStories,
        OfferRequirements.postOnInstagramStories3x,
        OfferRequirements.postOnInstagramFeed,
        OfferRequirements.postOnInstagramReels,
      ].includes(field.value as OfferRequirements) && (
        <>
          <Box mb={6}>
            <Box sx={{ mb: '15px', display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Typography
                sx={{
                  fontSize: '15px',
                  fontWeight: 500,
                }}
              >
                Type of post
              </Typography>
              {noPremiumAccess && (
                <PremiumTooltip showStarterIcon>
                  <Box display={'flex'} alignContent={'flex-start'} gap={'8px'}>
                    <StarterIcon sx={{ margin: '0', fontSize: '18px', fill: '#40E5AF' }} />
                    <Typography
                      fontSize={'15px'}
                      sx={{ textDecoration: 'underline', fontWeight: 500 }}
                    >
                      Premium
                    </Typography>
                  </Box>
                </PremiumTooltip>
              )}
            </Box>
            <ReqRadioGroup
              aria-labelledby='radio-group-requirement-label'
              name='radio-group-requirement'
              noPremiumAccess={noPremiumAccess}
            />
            {initialValues &&
              method === 'update' &&
              values.requirements !== initialValues.requirements && <AttentionError />}
          </Box>
          <Box>
            <MinimumFollowersOptions
              defaultValue={values.minFollowers || 0}
              options={fOptions}
              showTooltip={subscriptionPlan === 'basic'}
            />
            {initialValues &&
              method === 'update' &&
              initialValues.minFollowers !== values.minFollowers && <AttentionError />}
          </Box>
        </>
      )}
      {field.value !== 'noRequirement' && (
        <Box mt={5}>
          <SideBySide shouldFlexEnd>
            <CustomBox>
              <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                <Typography
                  sx={{
                    fontSize: '15px',
                    fontWeight: 500,
                  }}
                >
                  Incentive per Promotion (optional)
                </Typography>
              </Box>
              <Subtitle>Paid to the member after they post</Subtitle>
              <FormikNumberField
                id='txt-offer-value'
                name='incentiveValue'
                className='offer-input-field'
                placeholder='0'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <CurrencySign currency={venueCurrency || 'usd'} />
                    </InputAdornment>
                  ),
                }}
              />
            </CustomBox>
          </SideBySide>
        </Box>
      )}
    </>
  );
};

const CustomBox = styled.div`
  display: flex;
  flex-direction: column;
  fieldset {
    border: 1px solid #e6e6e6;
  }
`;

const Subtitle = styled.div`
  font-family: 'Poppins', 'Helvetica', 'Arial', sans-serif;
  font-weight: 400;
  font-size: 13px;
  letter-spacing: 0%;
  color: rgba(17, 17, 17, 0.8);
  padding-bottom: 8px;
  white-space: nowrap;
`;
